import { useState } from "react";
import { Cog, Link2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Pill } from "@/components/ui/pill";
import { Notify, toast } from "@/components/ui/notify";
import { isApiClientError } from "@/lib/api-client";
import type { SensorSummary } from "@/features/sensors/hooks";
import { useLines, useLineMachines } from "@/features/lines/hooks";
import { useParameters } from "@/features/line-editor/hooks";
import { useLinkSensorToParameter, useUnlinkSensorLink } from "./hooks/useSensorLinks";

function LinkPicker({ sensorId }: { sensorId: string }) {
  const [lineId, setLineId] = useState("");
  const [machineId, setMachineId] = useState("");
  const [parameterId, setParameterId] = useState("");
  const lines = useLines();
  const machines = useLineMachines(lineId);
  const params = useParameters(machineId);
  const link = useLinkSensorToParameter();

  async function onLink() {
    if (!parameterId) return;
    try {
      await link.mutateAsync({ parameterId, sensorId });
      toast.success("Sensor vinculado ao parâmetro.");
      setLineId("");
      setMachineId("");
      setParameterId("");
    } catch (err) {
      toast.error("Não foi possível vincular.", {
        description: isApiClientError(err) ? err.error.message : "Erro inesperado.",
      });
    }
  }

  return (
    <div className="grid grid-cols-1 gap-3 rounded-md border border-border-subtle bg-bg-muted-rd p-3 sm:grid-cols-3">
      <div className="space-y-1.5">
        <Label htmlFor="lk-line">Linha</Label>
        <Select
          id="lk-line"
          value={lineId}
          onChange={(e) => {
            setLineId(e.target.value);
            setMachineId("");
            setParameterId("");
          }}
        >
          <option value="">Selecione…</option>
          {(lines.data ?? []).map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="lk-machine">Máquina</Label>
        <Select
          id="lk-machine"
          value={machineId}
          disabled={!lineId}
          onChange={(e) => {
            setMachineId(e.target.value);
            setParameterId("");
          }}
        >
          <option value="">Selecione…</option>
          {(machines.data ?? []).map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="lk-param">Parâmetro</Label>
        <Select
          id="lk-param"
          value={parameterId}
          disabled={!machineId}
          onChange={(e) => setParameterId(e.target.value)}
        >
          <option value="">Selecione…</option>
          {(params.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
      </div>
      <div className="sm:col-span-3">
        <Button size="sm" onClick={() => void onLink()} disabled={!parameterId || link.isPending}>
          <Link2 className="h-4 w-4" />
          {link.isPending ? "Vinculando…" : "Vincular parâmetro"}
        </Button>
      </div>
    </div>
  );
}

export function SensorLinkSection({ sensor }: { sensor: SensorSummary }) {
  const unlink = useUnlinkSensorLink();
  const active = sensor.activeLink;

  async function onUnlink() {
    if (!active) return;
    const ok = await Notify.confirm({
      variant: "warning",
      title: "Remover vínculo do sensor?",
      description: `${active.machineName} · ${active.parameterName}`,
      confirmLabel: "Desvincular",
    });
    if (!ok) return;
    try {
      await unlink.mutateAsync(active.linkId);
      toast.success("Vínculo removido.");
    } catch (err) {
      toast.error("Não foi possível desvincular.", {
        description: isApiClientError(err) ? err.error.message : "Erro inesperado.",
      });
    }
  }

  return (
    <section className="space-y-2">
      <div className="text-[13px] font-medium text-text-secondary">Vínculo</div>
      {active ? (
        <Pill tone="brand" icon={<Cog className="h-3 w-3" />}>
          {active.machineName} · {active.parameterName}
          <span className="text-text-tertiary"> ({active.lineName})</span>
          <button
            type="button"
            onClick={() => void onUnlink()}
            aria-label="Remover vínculo"
            className="ml-1 rounded hover:text-rd-danger-700"
          >
            <X className="h-3 w-3" />
          </button>
        </Pill>
      ) : (
        <LinkPicker sensorId={sensor.id} />
      )}
    </section>
  );
}
